/**
 * Central regelsida-mall (SEO-mall). Renderar en enda regel med:
 * - Breadcrumb + H1
 * - Intro
 * - Regelutdrag (block 1)
 * - Exempel (block 2)
 * - Vanliga misstag (block 3)
 * - Kommunala avvikelser (block 4)
 * - FAQ (block 5)
 * - Internlänkar (block 6)
 * - JSON-LD: Article + FAQPage + HowTo + BreadcrumbList
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, AlertTriangle, CheckCircle2, MapPin, Sparkles, ArrowRight, BookOpen, ListChecks, ShieldCheck, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { combineJsonLd } from "@/lib/structuredData";
import { resolveRelated } from "@/content/rulebank";
import KaComparison from "@/components/KaComparison";

const SITE = "https://bygglovkollen.se";

function buildBreadcrumbLd(category, rule) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Start", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: category.label, item: `${SITE}/${category.slug}` },
      { "@type": "ListItem", position: 3, name: rule.h1 || rule.title, item: `${SITE}/${category.slug}/${rule.slug}` },
    ],
  };
}

function buildArticleLd(category, rule) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: rule.h1 || rule.title,
    description: rule.metaDescription || rule.intro,
    inLanguage: "sv-SE",
    author: { "@type": "Organization", name: "Bygglovskompassen" },
    publisher: {
      "@type": "Organization",
      name: "Bygglovskompassen",
      logo: { "@type": "ImageObject", url: `${SITE}/logo512.png` },
    },
    mainEntityOfPage: `${SITE}/${category.slug}/${rule.slug}`,
    articleSection: category.label,
    dateModified: rule.updated || "2026-07-04",
  };
}

function buildFaqLd(faq) {
  if (!faq?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

function buildHowToLd(rule) {
  if (!rule.howto?.steps?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: rule.howto.name || `Så gör du: ${rule.h1 || rule.title}`,
    description: rule.howto.description || rule.intro,
    step: rule.howto.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

export default function RulePageTemplate({ category, rule }) {
  const related = resolveRelated(rule.related);
  const jsonLd = combineJsonLd(
    buildBreadcrumbLd(category, rule),
    buildArticleLd(category, rule),
    buildFaqLd(rule.faq),
    buildHowToLd(rule)
  );

  // Verifierade lagtexter från Riksdagen (om rule.verified_source finns)
  const [verified, setVerified] = useState(null);
  const [verifiedError, setVerifiedError] = useState(false);
  useEffect(() => {
    if (!rule.verified_source) {
      setVerified(null);
      return;
    }
    const { sfs, chapter, paragraph } = rule.verified_source;
    let mounted = true;
    api
      .get(`/laws/${sfs}/${chapter}/${paragraph}`)
      .then((r) => {
        if (mounted) {
          setVerified(r.data);
          setVerifiedError(false);
        }
      })
      .catch(() => {
        if (mounted) setVerifiedError(true);
      });
    return () => {
      mounted = false;
    };
  }, [rule.slug, rule.verified_source]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
      <Seo
        title={rule.title}
        description={rule.metaDescription}
        path={`/${category.slug}/${rule.slug}`}
        keywords={rule.keywords}
        jsonLd={jsonLd}
      />

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-zinc-500 mb-6" aria-label="Brödsmulor" data-testid="breadcrumb">
        <Link to="/" className="hover:text-blue-700">Start</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to={`/${category.slug}`} className="hover:text-blue-700">{category.label}</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-zinc-900 font-medium truncate">{rule.h1 || rule.title}</span>
      </nav>

      {/* H1 + Intro */}
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-blue-700 mb-3">
        <Sparkles className="w-3.5 h-3.5" /> {category.short} · Regelbanken
      </div>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
        {rule.h1 || rule.title}
      </h1>
      <p className="mt-4 text-base sm:text-lg text-zinc-600 max-w-3xl leading-relaxed">{rule.intro}</p>

      {rule.slug === "kontrollansvarig" && <KaComparison />}

      {/* Block 1: Regelutdrag (verifierat om möjligt, annars manuell fallback) */}
      {(verified || rule.excerpt) && (
        <section
          className={`mt-10 border-l-4 rounded-r-sm px-5 py-4 ${
            verified ? "border-emerald-600 bg-emerald-50/60" : "border-blue-700 bg-blue-50/60"
          }`}
          data-testid="rule-excerpt"
        >
          <div
            className={`flex items-center justify-between gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold mb-1.5 ${
              verified ? "text-emerald-800" : "text-blue-800"
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" /> Regelutdrag
            </span>
            {verified && (
              <span
                className="inline-flex items-center gap-1 text-emerald-700 normal-case tracking-normal font-medium"
                data-testid="verified-badge"
                title={`Hämtat direkt från Riksdagens öppna data (${verified.law_version || "aktuell version"})`}
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Verifierad från Riksdagen
              </span>
            )}
          </div>
          <blockquote className="text-sm sm:text-base text-zinc-800 leading-relaxed italic">
            &ldquo;{verified ? verified.text : rule.excerpt.text}&rdquo;
          </blockquote>
          <div className="mt-2 flex items-center justify-between gap-2 text-xs text-zinc-600 flex-wrap">
            <span>— {verified ? verified.source : rule.excerpt?.source}</span>
            {verified && (
              <a
                href={verified.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
                data-testid="verified-source-link"
              >
                Källa: data.riksdagen.se <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          {verifiedError && rule.verified_source && (
            <div className="mt-2 text-xs text-amber-700">
              (Kunde inte hämta live-verifiering – visar cachad/manuell text)
            </div>
          )}
        </section>
      )}

      {/* Block 2: Exempel */}
      {rule.examples?.length > 0 && (
        <section className="mt-12" data-testid="examples">
          <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>Exempel</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rule.examples.map((ex, i) => (
              <div key={i} className="bg-white border border-zinc-200 rounded-sm p-4 hover:border-blue-300 transition">
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-2">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {ex.type}
                </div>
                <div className="text-sm font-medium text-zinc-900">{ex.title}</div>
                <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed">{ex.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Block 3: Vanliga misstag */}
      {rule.mistakes?.length > 0 && (
        <section className="mt-12" data-testid="mistakes">
          <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>Vanliga misstag</h2>
          <ul className="mt-4 space-y-2">
            {rule.mistakes.map((m, i) => (
              <li key={i} className="flex items-start gap-3 bg-red-50/50 border border-red-100 rounded-sm px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0 stroke-[1.75]" />
                <span className="text-sm text-zinc-800 leading-relaxed">{m}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Block 4: Kommunala avvikelser */}
      {rule.municipality_variations && (
        <section className="mt-12" data-testid="municipality-variations">
          <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>Kommunala avvikelser</h2>
          <div className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-sm px-4 py-4">
            <MapPin className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0 stroke-[1.75]" />
            <p className="text-sm text-zinc-800 leading-relaxed">{rule.municipality_variations}</p>
          </div>
        </section>
      )}

      {/* Block: Måtttabell (används på Attefall etc) */}
      {rule.measurements?.length > 0 && (
        <section className="mt-12" data-testid="measurements">
          <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>Mått & gränser</h2>
          <div className="mt-4 overflow-hidden border border-zinc-200 rounded-sm">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-700 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Egenskap</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Värde</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {rule.measurements.map((m, i) => (
                  <tr key={i} className="hover:bg-zinc-50">
                    <td className="px-4 py-3 text-zinc-700">{m.label}</td>
                    <td className="px-4 py-3 font-medium text-zinc-900">{m.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Block: HowTo-steps (rendered visibly + som schema) */}
      {rule.howto?.steps?.length > 0 && (
        <section className="mt-12" data-testid="howto">
          <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>
            {rule.howto.name || "Så gör du"}
          </h2>
          <ol className="mt-4 space-y-3">
            {rule.howto.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-4 bg-white border border-zinc-200 rounded-sm p-4">
                <div className="w-8 h-8 bg-blue-700 text-white text-sm font-semibold rounded-sm flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </div>
                <div>
                  <div className="text-sm font-semibold text-zinc-900">{s.name}</div>
                  <p className="mt-1 text-sm text-zinc-600 leading-relaxed">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Block 5: FAQ */}
      {rule.faq?.length > 0 && (
        <section className="mt-12" data-testid="rule-faq">
          <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>Vanliga frågor</h2>
          <div className="mt-4 divide-y divide-zinc-200 border border-zinc-200 rounded-sm bg-white">
            {rule.faq.map((it, i) => (
              <details key={i} className="group">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 px-5 py-4 hover:bg-zinc-50 transition">
                  <span className="text-sm sm:text-base font-medium text-zinc-900">{it.q}</span>
                  <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-5 pb-5 text-sm text-zinc-700 leading-relaxed">{it.a}</div>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Block 6: Internlänkar */}
      {related.length > 0 && (
        <section className="mt-14 pt-8 border-t border-zinc-200" data-testid="related-links">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">
            <ListChecks className="w-3.5 h-3.5" /> Relaterade regler
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {related.map((r) => (
              <Link
                key={r.path}
                to={r.path}
                data-testid={`related-${r.path.replaceAll("/", "-")}`}
                className="group flex items-center justify-between gap-3 bg-white border border-zinc-200 rounded-sm px-4 py-3 hover:border-blue-400 hover:shadow-sm transition"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{r.category}</div>
                  <div className="text-sm font-medium text-zinc-900 group-hover:text-blue-700 transition">{r.title}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-blue-700 transition flex-shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Disclaimer & källor */}
      <section className="mt-14 border border-zinc-200 rounded-sm bg-zinc-50 p-5" data-testid="disclaimer">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0 stroke-[1.75]" />
          <div className="text-sm text-zinc-700 leading-relaxed">
            <strong className="text-zinc-900">Notering:</strong> Sidans exempel och råd är en förenkling av gällande regelverk och ersätter inte juridisk rådgivning.
            {verified && <> Regelutdraget är hämtat direkt från Riksdagens öppna data ({verified.law_version || "aktuell version"}) och uppdateras automatiskt vid lagändring.</>}
            {!verified && rule.excerpt?.source && <> Regelutdraget är manuellt sammanställt – kontrollera alltid mot Riksdagens fulltext eller kommunens tolkning.</>}{" "}
            Kontakta alltid <strong>Boverket</strong> eller <strong>din kommun</strong> innan du fattar beslut.
            {rule.updated && (
              <span className="block mt-1.5 text-xs text-zinc-500">Senast granskad: {rule.updated}</span>
            )}
          </div>
        </div>
        <div className="mt-3 pl-7 flex flex-wrap gap-3 text-xs">
          <a
            href="https://www.boverket.se/sv/PBL-kunskapsbanken/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-700 hover:underline"
            data-testid="src-boverket"
          >
            Boverket – PBL-kunskapsbanken <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href="https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/plan-och-bygglag-2010900_sfs-2010-900/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-700 hover:underline"
            data-testid="src-riksdagen-pbl"
          >
            Riksdagen – PBL fulltext <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="mt-14 bg-zinc-900 text-white rounded-sm p-8 sm:p-10">
        <h3 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
          Osäker på vad som gäller för din tomt?
        </h3>
        <p className="mt-2 text-white/70 max-w-xl">
          Ställ en gratis fråga till vår AI-rådgivare eller kontrollera lovplikt med regelmotorn – inloggning krävs inte.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/chat" data-testid="cta-chat" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-5 py-3 rounded-sm text-sm font-medium">
            Ställ fråga gratis <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/guide/wizard" data-testid="cta-wizard" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-5 py-3 rounded-sm text-sm font-medium">
            Kontrollera lovplikt <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
